// The people half of a profile (wave 13): the card the player writes (bio, building now, links, pinned games and
// assets, banner), their posts, and Follow / Block for everyone else. /u/:handle (me-profile.ts) places these;
// /me/profile edits the card. Social decides what each viewer sees (minors' cards and posts: their audience only).

import { api } from "../api.ts";
import { gameById, getCatalogue } from "../catalogue.ts";
import { gameCard } from "../components.ts";
import { clear, h, link, toast } from "../dom.ts";
import { navigate, setTitle } from "../router.ts";
import { coverUrl } from "../rows.ts";
import { currentMe, signedIn } from "../session.ts";
import { peopleError, peopleStyles, postCard, type Card, type Link, type Post } from "../people.ts";

/** Known link hosts get their name as the label hint. Any https link is allowed. */
export function linkKind(url: string): string {
  try {
    const host = new URL(url).hostname.replace(/^www\./, "");
    if (/(^|\.)discord\.(gg|com)$/.test(host)) return "Discord";
    if (host === "x.com" || host === "twitter.com") return "X";
    if (host === "github.com") return "GitHub";
    if (host.endsWith("itch.io")) return "itch.io";
    if (host.endsWith("youtube.com") || host === "youtu.be") return "YouTube";
    return host;
  } catch {
    return "Link";
  }
}

/** "Discord" and "discord", "itch.io" and "itch": the label already says where the link goes. */
const sameName = (kind: string, label: string) => {
  const k = kind.toLowerCase().replace(/\.(io|com|gg)$/, "");
  const l = label.toLowerCase();
  return l === k || l.startsWith(k) || k.startsWith(l);
};

export interface ProfileExtras {
  card: Card | null;
  /** the banner image from the card (a game's key art), if any */
  banner: Promise<string | null>;
  about: HTMLElement | null;
  posts: HTMLElement;
  actions: HTMLElement | null;
}

export async function profileExtras(userId: string, self: boolean): Promise<ProfileExtras> {
  peopleStyles();
  const authed = signedIn();
  const card = await api<{ card: Card }>("social", authed ? `/profiles/${userId}` : `/public/profiles/${userId}`, { auth: authed }).then((r) => r.card, () => null);
  const banner = (async () => {
    const b = card?.banner;
    if (!b?.startsWith("game:")) return null;
    const cat = await getCatalogue().catch(() => null);
    const g = cat ? gameById(cat, b.slice(5)) : null;
    return g ? coverUrl(g, "full") : null;
  })();

  let about: HTMLElement | null = null;
  if (card?.visible && (card.bio || card.building_now || card.links.length || card.pinned.length)) {
    const pinned = h("ul", { class: "acct-strip" });
    if (card.pinned.length) {
      void getCatalogue()
        .then((cat) =>
          clear(
            pinned,
            card.pinned.map((p) => {
              if (p.kind === "game") {
                const g = gameById(cat, p.id);
                return g ? gameCard(g) : null;
              }
              const [creator, name] = p.id.split("/");
              return h("li", null, link(`/store/a/${creator}/${name}`, { class: "pin-asset" }, h("strong", null, name), h("span", { class: "dim small" }, `Asset by @${creator}`)));
            }),
          ),
        )
        .catch(() => {});
    }
    about = h(
      "section",
      { class: "acct-mod people-about" },
      card.building_now ? h("p", { class: "building" }, h("span", { class: "chip" }, "Building now"), " ", card.building_now) : null,
      card.bio ? h("p", { class: "bio" }, card.bio) : null,
      card.links.length
        ? h("ul", { class: "profile-links" }, card.links.map((l) => h("li", null, h("a", { href: l.url, target: "_blank", rel: "noopener nofollow ugc" }, sameName(linkKind(l.url), l.label) ? null : [h("span", { class: "link-kind" }, linkKind(l.url)), " "], l.label))))
        : null,
      card.pinned.length ? h("div", null, h("h2", null, "Pinned"), pinned) : null,
    );
  } else if (card && !card.visible && !self) {
    about = h("section", { class: "acct-mod" }, h("p", { class: "dim" }, "This player shares their profile with friends only."));
  }

  const list = h("div", { class: "posts" }, h("p", { class: "dim" }, "Loading…"));
  const posts = h("section", { class: "acct-mod people-posts" }, h("div", { class: "acct-mod-head" }, h("h2", null, "Bangers")), list);
  void api<{ posts: Post[] }>("social", authed ? `/users/${userId}/posts?limit=20` : `/public/users/${userId}/posts?limit=20`, { auth: authed })
    .then((r) =>
      clear(
        list,
        r.posts.length
          ? r.posts.map((p) => postCard(p, { permalink: true, onDelete: () => list.querySelector(`[data-post="${p.id}"]`)?.remove() }))
          : h("p", { class: "dim" }, self ? "No bangers yet. " : "No bangers to show.", self ? link("/feed", null, "Write one") : null),
      ),
    )
    .catch(() => clear(list, h("p", { class: "dim" }, "Bangers couldn't load.")));

  let actions: HTMLElement | null = null;
  if (self) actions = h("div", { class: "acct-row" }, link("/me/profile", { class: "btn" }, "Edit profile"), link("/me/avatar", { class: "btn ghost" }, "Edit avatar"));
  else if (authed) actions = followBlock(userId);
  else actions = h("div", { class: "acct-row" }, link(`/signin?next=${encodeURIComponent(location.pathname)}`, { class: "btn" }, "Sign in to follow"));
  return { card, banner, about, posts, actions };
}

function followBlock(userId: string): HTMLElement {
  const row = h("div", { class: "acct-row" });
  const draw = async () => {
    const [following, blocked] = await Promise.all([
      api<{ following: { user_id: string }[] }>("social", "/follows").then((r) => r.following.some((f) => f.user_id === userId), () => false),
      api<{ blocked: { user_id: string }[] }>("social", "/blocks").then((r) => r.blocked.some((b) => b.user_id === userId), () => false),
    ]);
    const call = (method: string, path: string, done: string) => async () => {
      try {
        await api("social", path, { method });
        toast(done);
        void draw();
      } catch (e) {
        toast(peopleError(e));
      }
    };
    clear(
      row,
      blocked
        ? h("button", { type: "button", class: "btn ghost", onclick: call("DELETE", `/blocks/${userId}`, "Unblocked.") }, "Unblock")
        : [
            h("button", { type: "button", class: following ? "btn ghost" : "btn", onclick: call(following ? "DELETE" : "PUT", `/follows/${userId}`, following ? "Unfollowed." : "Following.") }, following ? "Following" : "Follow"),
            h(
              "button",
              {
                type: "button",
                class: "btn ghost",
                onclick: () => {
                  if (confirm("Block this player? You won't see each other's bangers, and they can't follow, friend or message you.")) void call("PUT", `/blocks/${userId}`, "Blocked.")();
                },
              },
              "Block",
            ),
          ],
    );
  };
  void draw();
  return row;
}

// ---- /me/profile: edit your card ----

const PRESETS: [string, string][] = [
  ["preset:night", "Night"],
  ["preset:dawn", "Dawn"],
  ["preset:arcade", "Arcade"],
];

export async function editProfilePage(): Promise<HTMLElement> {
  peopleStyles();
  setTitle("Edit profile");
  if (!signedIn()) {
    navigate(`/signin?next=${encodeURIComponent("/me/profile")}`);
    return h("div");
  }
  const me = currentMe();
  const card = (await api<{ card: Card }>("social", "/profile")).card;
  const cat = await getCatalogue().catch(() => null);
  const gameTitle = (id: string) => (cat ? gameById(cat, id)?.title : null) ?? id;
  const titleToId = new Map((cat?.games ?? []).map((g) => [g.title.toLowerCase(), g.id]));
  const msg = h("div", { "aria-live": "polite" });

  const bio = h("textarea", { name: "bio", rows: 4, maxlength: 500, placeholder: "Who you are, what you make, what you play." }, card.bio ?? "");
  const building = h("input", { name: "building_now", maxlength: 120, placeholder: "A kart racer set in a lighthouse", value: card.building_now ?? "" });
  const linkRows = h("div", { class: "link-rows" });
  const addLink = (l: Link = { label: "", url: "" }) => {
    if (linkRows.children.length >= 8) return;
    const row = h(
      "div",
      { class: "link-row" },
      h("input", { name: "label", maxlength: 40, placeholder: "Label (Discord, itch, My site)", value: l.label, "aria-label": "Link label" }),
      h("input", { name: "url", type: "url", placeholder: "https://", value: l.url, "aria-label": "Link address" }),
      h("button", { type: "button", class: "btn small ghost", "aria-label": "Remove link", onclick: () => row.remove() }, "Remove"),
    );
    linkRows.append(row);
  };
  card.links.forEach(addLink);
  if (!card.links.length) addLink();

  const pinList = h("ul", { class: "pin-list" });
  const pins: Card["pinned"] = [...card.pinned];
  const drawPins = () =>
    clear(
      pinList,
      pins.map((p, i) => h("li", null, h("span", null, p.kind === "game" ? gameTitle(p.id) : p.id), h("button", { type: "button", class: "btn small ghost", onclick: () => (pins.splice(i, 1), drawPins()) }, "Unpin"))),
    );
  drawPins();
  const pinInput = h("input", { list: "pin-games", placeholder: "A game title, or an asset as creator/name", "aria-label": "Pin a game or asset" });
  const pinOptions = h("datalist", { id: "pin-games" }, (cat?.games ?? []).slice(0, 400).map((g) => h("option", { value: g.title })));

  const banner = h("select", { name: "banner", "aria-label": "Banner" }, h("option", { value: "" }, "Default"), PRESETS.map(([v, t]) => h("option", { value: v, selected: card.banner === v }, t)), (cat?.games ?? []).slice(0, 400).map((g) => h("option", { value: `game:${g.id}`, selected: card.banner === `game:${g.id}` }, `Key art: ${g.title}`)));

  const field = (label: string, el: HTMLElement, hint?: string) => h("label", { class: "field" }, h("span", { class: "field-label" }, label), el, hint ? h("span", { class: "dim small" }, hint) : null);
  return h(
    "div",
    { class: "people edit-profile" },
    link(me?.handle ? `/u/${me.handle}` : "/me", { class: "back" }, "← Your profile"),
    h("h1", null, "Edit profile"),
    h("p", { class: "dim" }, me?.ageBand === "13-17" ? "Your profile is shown to your friends and the people you follow back." : "Your profile is public unless you change it in Settings → Privacy."),
    h(
      "form",
      {
        class: "edit-form",
        onsubmit: async (e: Event) => {
          e.preventDefault();
          const links = [...linkRows.querySelectorAll(".link-row")]
            .map((r) => ({ label: (r.querySelector("[name=label]") as HTMLInputElement).value.trim(), url: (r.querySelector("[name=url]") as HTMLInputElement).value.trim() }))
            .filter((l) => l.url)
            .map((l) => ({ label: l.label || linkKind(l.url), url: l.url }));
          const b = (banner as HTMLSelectElement).value;
          try {
            await api("social", "/profile", {
              method: "PATCH",
              body: { bio: (bio as HTMLTextAreaElement).value.trim() || null, building_now: (building as HTMLInputElement).value.trim() || null, links, pinned: pins, banner: b || null },
            });
            clear(msg, h("p", { class: "ok-line", role: "status" }, "Saved."));
          } catch (x) {
            clear(msg, h("p", { class: "error", role: "alert" }, peopleError(x)));
          }
        },
      },
      field("Bio", bio, "Up to 500 characters. Plain text."),
      field("Building now", building, "One line about what you're working on."),
      h("fieldset", { class: "field" }, h("legend", { class: "field-label" }, "Links"), h("span", { class: "dim small" }, "Discord, X, GitHub, itch, your website: any https link, up to 8."), linkRows, h("button", { type: "button", class: "btn small ghost", onclick: () => addLink() }, "Add a link")),
      h(
        "fieldset",
        { class: "field" },
        h("legend", { class: "field-label" }, "Pinned games and assets"),
        pinList,
        h(
          "div",
          { class: "composer-row" },
          pinInput,
          pinOptions,
          h(
            "button",
            {
              type: "button",
              class: "btn small",
              onclick: () => {
                const v = (pinInput as HTMLInputElement).value.trim();
                if (!v || pins.length >= 6) return;
                const gid = titleToId.get(v.toLowerCase()) ?? (/^g_[a-z0-9-]+$/.test(v) ? v : null);
                if (gid) pins.push({ kind: "game", id: gid });
                else if (/^[a-z0-9][a-z0-9-]{1,38}\/[a-z0-9][a-z0-9-]{1,62}$/.test(v)) pins.push({ kind: "asset", id: v });
                else return toast("Pick a game from the list, or type an asset as creator/name.");
                (pinInput as HTMLInputElement).value = "";
                drawPins();
              },
            },
            "Pin",
          ),
        ),
        h("span", { class: "dim small" }, "Up to 6."),
      ),
      field("Banner", banner, "A colour, or the key art of a game on Banger."),
      h("div", { class: "actions" }, h("button", { class: "btn", type: "submit" }, "Save")),
      msg,
    ),
  );
}
